/**
 * Who is at the school, and what they may do.
 *
 * Invitations rather than administrator-set passwords: an administrator inventing
 * a colleague's password means it is known to two people from the start, and a
 * school administrator is not the right person to be handling anyone's
 * credentials.
 *
 * Nobody is deleted, only deactivated. A teacher who leaves still wrote papers
 * sitting in the print queue and in the audit trail.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { Person, Role } from '../../lib/api/client'
import {
  Button,
  Checkbox,
  Dialog,
  Field,
  Input,
  Pill,
  Select,
  Spinner,
} from '../../components/ui'
import { Icon } from '../../components/icons'
import { toast } from '../../components/toast'
import { invitationUrl } from '../../lib/invitationUrl'
import { useAuth } from '../../app/useAuth'
import { RolePills, roleDisplayLabel } from './Settings'

export function People({ schoolId }: { schoolId?: string }) {
  const queryClient = useQueryClient()
  const { user, refresh: refreshAccount } = useAuth()
  const [inviting, setInviting] = useState(false)
  const [editing, setEditing] = useState<Person | null>(null)

  const scope = schoolId ?? 'current'
  const people = useQuery({
    queryKey: ['people', scope],
    queryFn: () => api.people(schoolId),
  })
  const invitations = useQuery({
    queryKey: ['invitations', scope],
    queryFn: () => api.invitations(schoolId),
  })
  const labels = useQuery({ queryKey: ['roles'], queryFn: api.roles })
  const school = useQuery({
    queryKey: ['school', scope],
    queryFn: () => api.school(schoolId),
  })

  const refresh = (changedPersonId?: string) => {
    queryClient.invalidateQueries({ queryKey: ['people', scope] })
    queryClient.invalidateQueries({ queryKey: ['invitations', scope] })
    queryClient.invalidateQueries({ queryKey: ['school', scope] })
    queryClient.invalidateQueries({ queryKey: ['platform', 'schools'] })
    // Changing your own roles changes what the navigation should show. Nothing
    // else tells the auth context that, so an administrator who gives themselves
    // the reviewer role would watch the Review tab stay hidden until they
    // happened to reload the page.
    if (changedPersonId && changedPersonId === user?.id) void refreshAccount()
  }
  const failed = (error: Error) => toast(error.message, 'error')

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.setActive(id, active, schoolId),
    onSuccess: (_data, variables) => refresh(variables.id),
    onError: failed,
  })

  const cancelInvite = useMutation({
    mutationFn: (id: string) => api.cancelInvitation(id, schoolId),
    // Not `onSuccess: refresh` - onSuccess passes the mutation result as the
    // first argument, and refresh's first argument is now a person id. Cancelling
    // an invitation cannot change your own roles, so there is nothing to pass.
    onSuccess: () => refresh(),
    onError: failed,
  })

  const setDefaultReviewer = useMutation({
    mutationFn: (id: string | null) => api.setDefaultReviewer(id, schoolId),
    onSuccess: () => {
      refresh()
      toast('Default reviewer saved.')
    },
    onError: failed,
  })

  if (people.isLoading) return <Spinner label="Loading the people at your school" />

  const roleLabels: Record<string, Role> = labels.data ?? {}
  const reviewers = (people.data ?? []).filter((p) => p.roles.includes('reviewer') && p.is_active)

  return (
    <div className="tc-panel">
      <div className="tc-panel-head">
        <h2>People</h2>
        <div className="tc-spacer" />
        <Button variant="primary" size="sm" icon={<Icon.plus size={15} />} onClick={() => setInviting(true)}>
          Invite a colleague
        </Button>
      </div>

      <div className="tc-table-wrap tc-people-table-wrap">
      <table className="tc-table tc-people-table">
        <thead>
          <tr>
            <th>Name</th>
            <th style={{ width: 220 }}>Email</th>
            <th>Roles</th>
            <th style={{ width: 110 }}>Status</th>
            <th style={{ width: 170 }}>
              <span className="tc-sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {(people.data ?? []).map((person) => (
            <tr key={person.id}>
              <td data-label="Name">{person.full_name}</td>
              <td data-label="Email" style={{ color: 'var(--tc-muted)' }}>{person.email}</td>
              <td data-label="Roles">
                <RolePills roles={person.roles} labels={roleLabels} />
              </td>
              <td data-label="Status">
                {person.is_active ? (
                  <Pill tone="success">Active</Pill>
                ) : (
                  <Pill tone="muted">Deactivated</Pill>
                )}
              </td>
              <td data-label="Actions">
                <div className="tc-row-actions">
                  <Button
                    size="sm"
                    icon={<Icon.edit size={14} />}
                    onClick={() => setEditing(person)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant={person.is_active ? 'danger' : 'secondary'}
                    onClick={() => setActive.mutate({ id: person.id, active: !person.is_active })}
                  >
                    {person.is_active ? 'Deactivate' : 'Reactivate'}
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <div className="tc-panel-head" style={{ marginTop: 'var(--tc-4)' }}>
        <h2>Default reviewer</h2>
      </div>
      <p className="tc-field-hint" style={{ margin: 0 }}>
        Pre-selected when a teacher sends a paper for review, so the usual case is one click.
      </p>
      <Select
        aria-label="Default reviewer"
        style={{ maxWidth: 280 }}
        value={school.data?.default_reviewer_id ?? ''}
        onChange={(event) => setDefaultReviewer.mutate(event.target.value || null)}
      >
        <option value="">No default</option>
        {reviewers.map((person) => (
          <option key={person.id} value={person.id}>
            {person.full_name}
          </option>
        ))}
      </Select>

      {(invitations.data ?? []).length > 0 && (
        <>
          <div className="tc-panel-head" style={{ marginTop: 'var(--tc-4)' }}>
            <h2>Invitations not yet accepted</h2>
          </div>
          <div className="tc-table-wrap tc-people-table-wrap">
          <table className="tc-table tc-people-table">
            <thead>
              <tr>
                <th style={{ width: 240 }}>Email</th>
                <th>Roles</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 200 }}>Link</th>
                <th style={{ width: 90 }}>
                  <span className="tc-sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(invitations.data ?? []).map((invite) => (
                <tr key={invite.id}>
                  <td data-label="Email">{invite.email}</td>
                  <td data-label="Roles">
                    <RolePills roles={invite.roles} labels={roleLabels} />
                  </td>
                  <td data-label="Status">
                    {invite.has_expired ? (
                      <Pill tone="warning">Expired</Pill>
                    ) : (
                      <Pill tone="muted">Waiting</Pill>
                    )}
                  </td>
                  <td data-label="Link">
                    <Button
                      size="sm"
                      onClick={() => {
                        const link = invitationUrl(invite.token)
                        navigator.clipboard
                          ?.writeText(link)
                          .then(() => toast('Invitation link copied.'))
                          .catch(() => toast(link))
                      }}
                    >
                      Copy link
                    </Button>
                  </td>
                  <td data-label="Actions">
                    <Button size="sm" variant="danger" onClick={() => cancelInvite.mutate(invite.id)}>
                      Withdraw
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}

      {inviting && (
        <InviteDialog
          roleLabels={roleLabels}
          schoolId={schoolId}
          onClose={() => setInviting(false)}
          onDone={() => refresh()}
        />
      )}
      {editing && (
        <EditPersonDialog
          person={editing}
          roleLabels={roleLabels}
          schoolId={schoolId}
          onClose={() => setEditing(null)}
          onDone={() => refresh(editing.id)}
        />
      )}
    </div>
  )
}

function InviteDialog({
  roleLabels,
  schoolId,
  onClose,
  onDone,
}: {
  roleLabels: Record<string, Role>
  schoolId?: string
  onClose: () => void
  onDone: () => void
}) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [roles, setRoles] = useState<string[]>(['teacher'])
  const [link, setLink] = useState<string | null>(null)

  const invite = useMutation({
    mutationFn: () => api.invite({ email, full_name: fullName, roles }, schoolId),
    onSuccess: (created) => {
      // Shown rather than emailed: there is no mail server, and pretending an
      // invitation was sent when nothing left the building would be worse than
      // asking the administrator to paste a link.
      setLink(invitationUrl(created.token))
      onDone()
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  return (
    <Dialog
      open
      title="Invite a colleague"
      onClose={onClose}
      footer={
        link ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!email.trim() || invite.isPending}
              onClick={() => invite.mutate()}
            >
              Create invitation
            </Button>
          </>
        )
      }
    >
      {link ? (
        <>
          <p style={{ margin: 0, fontSize: 'var(--tc-text-sm)' }}>
            Send this link to {email}. It works once and expires in two weeks.
          </p>
          <Input readOnly value={link} onFocus={(event) => event.currentTarget.select()} />
          <Button
            size="sm"
            onClick={() =>
              navigator.clipboard?.writeText(link).then(() => toast('Copied.')).catch(() => {})
            }
          >
            Copy link
          </Button>
        </>
      ) : (
        <>
          <Field label="Email">
            <Input
              type="email"
              autoFocus
              value={email}
              placeholder="colleague@school.com"
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Field label="Their name" hint="Optional — they can set it themselves.">
            <Input value={fullName} onChange={(event) => setFullName(event.target.value)} />
          </Field>
          <RoleChoices roleLabels={roleLabels} roles={roles} onChange={setRoles} />
        </>
      )}
    </Dialog>
  )
}

function EditPersonDialog({
  person,
  roleLabels,
  schoolId,
  onClose,
  onDone,
}: {
  person: Person
  roleLabels: Record<string, Role>
  schoolId?: string
  onClose: () => void
  onDone: () => void
}) {
  const [fullName, setFullName] = useState(person.full_name)
  // The owner role is not assignable, so it is not offered as a choice - and the
  // server keeps it regardless of what is sent.
  const [roles, setRoles] = useState(
    person.roles.filter((role) => roleLabels[role]?.assignable),
  )

  const save = useMutation({
    mutationFn: () =>
      api.updatePerson(person.id, { full_name: fullName.trim(), roles }, schoolId),
    onSuccess: () => {
      onDone()
      onClose()
      toast('Person updated.')
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  return (
    <Dialog
      open
      title={`Edit ${person.full_name}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!fullName.trim() || roles.length === 0 || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      <Field label="Name">
        <Input
          autoFocus
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
        />
      </Field>
      <Field label="Email" hint="The email is their login identity and cannot be changed here.">
        <Input readOnly value={person.email} />
      </Field>
      <RoleChoices roleLabels={roleLabels} roles={roles} onChange={setRoles} />
    </Dialog>
  )
}

function RoleChoices({
  roleLabels,
  roles,
  onChange,
}: {
  roleLabels: Record<string, Role>
  roles: string[]
  onChange: (roles: string[]) => void
}) {
  return (
    <fieldset className="tc-roles">
      <legend className="tc-field-label">What may they do?</legend>
      {Object.entries(roleLabels)
        .filter(([, role]) => role.assignable)
        .map(([key]) => (
        <Checkbox
          key={key}
          label={roleDisplayLabel(key, roleLabels)}
          checked={roles.includes(key)}
          onChange={(event) =>
            onChange(
              event.target.checked ? [...roles, key] : roles.filter((role) => role !== key),
            )
          }
        />
      ))}
    </fieldset>
  )
}
